import React from "react";
import { Info, Plus, Trash2, UserPlus, Users, Zap } from "lucide-react";
import { STAKE_STEP_META } from "./p2pCreateConstants";
import {
  MIN_COUNTER_STAKE,
  maxTotalCounterStakeFromBudget,
} from "../../../utils/p2pHelpers";

const P2PCreateStakesStep = ({
  step,
  amount,
  onAmountChange,
  offeredOdds,
  onOfferedOddsChange,
  minStake,
  onMinStakeChange,
  hasInvite,
  onHasInviteChange,
  inviteCodes,
  onInviteCodesChange,
  maxPeople,
  onMaxPeopleChange,
}) => {
  const meta = STAKE_STEP_META[step - 1];
  const budgetNum = Number(amount) || 0;
  const oddsNum = Number(offeredOdds) || 0;
  const minStakeNum = Number(minStake) || 0;
  const counterCapacity =
    budgetNum > 0 && oddsNum > 1
      ? maxTotalCounterStakeFromBudget(budgetNum, oddsNum)
      : 0;
  const seatLiability =
    minStakeNum > 0 && oddsNum > 1
      ? Math.round(minStakeNum * (oddsNum - 1) * 100) / 100
      : 0;
  const capacityOk = counterCapacity >= MIN_COUNTER_STAKE;
  const stakeFits =
    capacityOk &&
    minStakeNum >= MIN_COUNTER_STAKE &&
    minStakeNum <= counterCapacity + 0.001;
  const invitedCount = hasInvite
    ? inviteCodes.map((c) => c.trim()).filter(Boolean).length
    : 0;
  const allowPublic = parseInt(maxPeople || "0", 10) > 0;

  const addInvite = () => {
    if (inviteCodes.length < 5) onInviteCodesChange([...inviteCodes, ""]);
  };

  const removeInvite = (idx) => {
    if (inviteCodes.length > 1) {
      onInviteCodesChange(inviteCodes.filter((_, i) => i !== idx));
    }
  };

  const changeInvite = (idx, value) => {
    onInviteCodesChange(inviteCodes.map((c, i) => (i === idx ? value : c)));
  };

  return (
    <div className="pcm2-stakes">
      <div className="pcm2-stakes-intro">
        <p className="pcm2-outcome-kicker">Step {step + 1}: {meta.title}</p>
        <p className="pcm2-stakes-hint">
          <Info size={13} aria-hidden />
          {meta.hint}
        </p>
      </div>

      {step === 1 ? (
        <div className="pcm2-stake-amount">
          <label className="pcm2-field-label" htmlFor="pcm2-amount">
            Creator budget
          </label>
          <div className="pcm2-amount-wrap">
            <span className="pcm2-amount-badge">Cash</span>
            <input
              id="pcm2-amount"
              type="number"
              inputMode="numeric"
              min={500}
              step={100}
              placeholder="500"
              value={amount}
              onChange={(e) => onAmountChange(e.target.value.replace(/[^0-9.]/g, ""))}
            />
          </div>
          <label className="pcm2-field-label" htmlFor="pcm2-odds" style={{ marginTop: 16 }}>
            Offered odds (for counters)
          </label>
          <div className="pcm2-amount-wrap">
            <input
              id="pcm2-odds"
              type="number"
              inputMode="decimal"
              min={1.01}
              max={50}
              step={0.01}
              placeholder="2.00"
              value={offeredOdds}
              onChange={(e) => onOfferedOddsChange(e.target.value.replace(/[^0-9.]/g, ""))}
            />
          </div>
          <div className="pcm2-quick-picks" role="group" aria-label="Odds quick picks">
            {["1.5", "2", "2.5", "3"].map((preset) => {
              const active = Number(offeredOdds) === Number(preset);
              return (
                <button
                  key={preset}
                  type="button"
                  className={`pcm2-quick-pick${active ? " is-active" : ""}`}
                  onClick={() => onOfferedOddsChange(preset)}
                >
                  {Number(preset).toFixed(2)}
                </button>
              );
            })}
          </div>
          <label className="pcm2-field-label" htmlFor="pcm2-min-stake" style={{ marginTop: 16 }}>
            Min counter stake
          </label>
          <div className="pcm2-amount-wrap">
            <span className="pcm2-amount-badge">Cash</span>
            <input
              id="pcm2-min-stake"
              type="number"
              inputMode="numeric"
              min={MIN_COUNTER_STAKE}
              step={100}
              placeholder={String(MIN_COUNTER_STAKE)}
              value={minStake}
              onChange={(e) => onMinStakeChange(e.target.value.replace(/[^0-9.]/g, ""))}
            />
          </div>
          <div className="pcm2-quick-picks" role="group" aria-label="Min stake quick picks">
            {["500", "1000", "2000", "5000"].map((preset) => {
              const presetNum = Number(preset);
              const active = Number(minStake) === presetNum;
              const affordable = capacityOk && presetNum <= counterCapacity + 0.001;
              return (
                <button
                  key={preset}
                  type="button"
                  className={`pcm2-quick-pick${active ? " is-active" : ""}${
                    !affordable ? " is-disabled" : ""
                  }`}
                  onClick={() => affordable && onMinStakeChange(preset)}
                  disabled={!affordable}
                >
                  {presetNum.toLocaleString()}
                </button>
              );
            })}
          </div>
          {budgetNum >= 500 && oddsNum >= 1.01 ? (
            <div
              className={`pcm2-amount-preview${
                !stakeFits ? " pcm2-amount-preview-warn" : ""
              }`}
            >
              <Zap size={14} aria-hidden />
              <span>
                {!capacityOk ? (
                  <>
                    Budget too small at these odds — need at least{" "}
                    <strong>
                      Cash {Math.ceil(MIN_COUNTER_STAKE * (oddsNum - 1)).toLocaleString()}
                    </strong>{" "}
                    to cover min stake {MIN_COUNTER_STAKE}.
                  </>
                ) : stakeFits ? (
                  <>
                    One min seat costs you <strong>Cash {seatLiability.toLocaleString()}</strong>{" "}
                    if they win · max min stake{" "}
                    <strong>Cash {counterCapacity.toLocaleString()}</strong>
                  </>
                ) : (
                  <>
                    Min stake needs <strong>Cash {seatLiability.toLocaleString()}</strong> cover,
                    but budget is only Cash {budgetNum.toLocaleString()} (max{" "}
                    <strong>Cash {counterCapacity.toLocaleString()}</strong>)
                  </>
                )}
              </span>
            </div>
          ) : (
            <p className="pcm2-field-note">
              Minimum budget Cash 500 · odds 1.01–50 · min stake Cash {MIN_COUNTER_STAKE}+
            </p>
          )}
        </div>
      ) : null}

      {step === 2 ? (
        <div className="pcm2-choice-stack">
          <button
            type="button"
            className={`pcm2-choice-card${hasInvite === true ? " pcm2-choice-card-on" : ""}`}
            onClick={() => onHasInviteChange(true)}
          >
            <span className="pcm2-choice-icon">
              <UserPlus size={18} />
            </span>
            <span className="pcm2-choice-copy">
              <strong>Yes, I have invite codes</strong>
              <span>Invite friends directly — optionally list publicly too</span>
            </span>
          </button>
          <button
            type="button"
            className={`pcm2-choice-card${hasInvite === false ? " pcm2-choice-card-on" : ""}`}
            onClick={() => onHasInviteChange(false)}
          >
            <span className="pcm2-choice-icon">
              <Users size={18} />
            </span>
            <span className="pcm2-choice-copy">
              <strong>Public only</strong>
              <span>Anyone on the board can counter</span>
            </span>
          </button>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="pcm2-invite-list">
          {inviteCodes.map((code, idx) => (
            <div key={idx} className="pcm2-invite-row">
              <span className="pcm2-invite-num">{idx + 1}</span>
              <input
                type="text"
                placeholder={`Friend code ${idx + 1}`}
                value={code}
                onChange={(e) => changeInvite(idx, e.target.value)}
                autoComplete="off"
              />
              {inviteCodes.length > 1 ? (
                <button
                  type="button"
                  className="pcm2-invite-remove"
                  onClick={() => removeInvite(idx)}
                  aria-label={`Remove friend ${idx + 1}`}
                >
                  <Trash2 size={14} />
                </button>
              ) : null}
            </div>
          ))}
          {inviteCodes.length < 5 ? (
            <button type="button" className="pcm2-invite-add" onClick={addInvite}>
              <Plus size={16} />
              Add another friend
            </button>
          ) : null}
        </div>
      ) : null}

      {step === 4 ? (
        <div className="pcm2-slots-block">
          {hasInvite ? (
            <label className="pcm2-consent-row">
              <input
                type="checkbox"
                checked={allowPublic}
                onChange={(e) => onMaxPeopleChange(e.target.checked ? "1" : "0")}
              />
              <span>Also list publicly (anyone can counter)</span>
            </label>
          ) : null}

          {budgetNum >= 500 && oddsNum >= 1.01 ? (
            <div className="pcm2-summary">
              <p className="pcm2-summary-title">Offer summary</p>
              <div className="pcm2-summary-grid">
                <div>
                  <span>Budget locked</span>
                  <strong>Cash {budgetNum.toLocaleString()}</strong>
                </div>
                <div>
                  <span>Offered odds</span>
                  <strong>{oddsNum}</strong>
                </div>
                <div>
                  <span>Min stake</span>
                  <strong>Cash {(minStakeNum || MIN_COUNTER_STAKE).toLocaleString()}</strong>
                </div>
                <div>
                  <span>Counter capacity</span>
                  <strong>Cash {counterCapacity.toLocaleString()}</strong>
                </div>
                {hasInvite ? (
                  <div>
                    <span>Invites</span>
                    <strong>{invitedCount}</strong>
                  </div>
                ) : null}
                <div className="pcm2-summary-highlight">
                  <span>Join mode</span>
                  <strong>
                    {!hasInvite
                      ? "Public"
                      : allowPublic
                        ? "Invite + public"
                        : "Invite only"}
                  </strong>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default P2PCreateStakesStep;
